import { useEffect, useState, useCallback } from "react";
import { Plus, Trash, Recycle, Wrench, CurrencyDollar, ChartLineDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, StatCard, MobileRow } from "@/components/Shared";
import { ImageCaptionField } from "@/components/ImageCaptionField";
import { CaptionedImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";

const REWORK_STATUSES = ["open", "in_progress", "passed", "scrapped"];
const REWORK_REASONS = ["defect", "damage", "wrong_spec", "assembly_error", "other"];
const SCRAP_REASONS = ["defect", "damage", "offcut", "expired", "rework_failed", "other"];

const EMPTY_REWORK = {
  part_name: "", item_id: "", quantity: 1, unit: "pcs", reason: "defect", area: "",
  labour_minutes: 0, labour_rate: 0, notes: "", image_id: null, image_caption: "",
};
const EMPTY_SCRAP = {
  item_id: "", item_name: "", quantity: 1, unit: "pcs", reason: "defect", area: "",
  notes: "", deduct_stock: true, image_id: null, image_caption: "",
};

export default function ReworkScrap() {
  const { user } = useAuth();
  const [view, setView] = useState("rework");
  const [rework, setRework] = useState([]);
  const [scrap, setScrap] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [reworkOpen, setReworkOpen] = useState(false);
  const [scrapOpen, setScrapOpen] = useState(false);
  const [rForm, setRForm] = useState(EMPTY_REWORK);
  const [sForm, setSForm] = useState(EMPTY_SCRAP);

  const load = useCallback(async () => {
    const [r, s, sum, it] = await Promise.all([
      api.get("/rework"),
      api.get("/scrap"),
      api.get("/scrap/summary"),
      api.get("/items"),
    ]);
    setRework(r.data);
    setScrap(s.data);
    setSummary(sum.data);
    setItems(it.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submitRework = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rework", {
        ...rForm,
        item_id: rForm.item_id || null,
        quantity: Number(rForm.quantity),
        labour_minutes: Number(rForm.labour_minutes),
        labour_rate: Number(rForm.labour_rate),
      });
      toast.success("Rework job logged");
      setReworkOpen(false);
      setRForm(EMPTY_REWORK);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const submitScrap = async (e) => {
    e.preventDefault();
    try {
      await api.post("/scrap", {
        ...sForm,
        item_id: sForm.item_id || null,
        quantity: Number(sForm.quantity),
      });
      toast.success(sForm.deduct_stock ? "Scrap logged and stock deducted" : "Scrap logged");
      setScrapOpen(false);
      setSForm(EMPTY_SCRAP);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setReworkStatus = async (id, status) => {
    try {
      await api.patch(`/rework/${id}/status`, { status });
      toast.success(
        status === "scrapped" ? "Marked scrapped — material logged as scrap" : `Marked ${status}`
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeScrap = async (id) => {
    try {
      await api.delete(`/scrap/${id}`);
      toast.success("Scrap entry removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeRework = async (id) => {
    try {
      await api.delete(`/rework/${id}`);
      toast.success("Rework job removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const pickItem = (id) => {
    const item = items.find((i) => i.id === id);
    setSForm((f) => ({
      ...f,
      item_id: id,
      item_name: item?.name || f.item_name,
      unit: item?.unit || f.unit,
    }));
  };

  const openRework = rework.filter((r) => r.status === "open" || r.status === "in_progress").length;

  return (
    <div data-testid="rework-scrap-page">
      <PageHeader
        overline="Quality & waste"
        title="Rework & Scrap"
        description="Log parts that need redoing, move them through the rework queue, and record scrapped material with the cost it took out of stock."
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="add-rework-button"
              onClick={() => setReworkOpen(true)}
              variant="outline"
              className="h-11 flex-1 rounded-none border-slate-300 text-xs font-semibold uppercase tracking-widest hover:bg-slate-100 sm:h-10 sm:flex-none"
            >
              <Wrench size={16} className="mr-2" /> Log rework
            </Button>
            <Button
              data-testid="add-scrap-button"
              onClick={() => setScrapOpen(true)}
              className="h-11 flex-1 rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:flex-none"
            >
              <Plus size={16} className="mr-2" /> Log scrap
            </Button>
          </div>
        }
      />

      {summary && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <StatCard testid="scrap-stat-open-rework" label="Open rework" value={openRework} icon={Wrench} sub="open + in progress" />
          <StatCard testid="scrap-stat-month-value" label="Scrap this month" value={`$${Number(summary.value_this_month).toLocaleString()}`} icon={CurrencyDollar} tone="danger" sub={`${summary.entries_this_month} entries`} />
          <StatCard testid="scrap-stat-rate" label="Scrap rate" value={`${summary.scrap_rate_pct}%`} icon={ChartLineDown} tone="danger" sub="of stock value this month" />
          <StatCard testid="scrap-stat-total-value" label="Scrap all time" value={`$${Number(summary.total_value).toLocaleString()}`} icon={Recycle} sub={`${summary.total_entries} entries`} />
        </div>
      )}

      <Tabs value={view} onValueChange={setView} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          <TabsTrigger data-testid="view-rework" value="rework" className="rounded-none text-xs uppercase tracking-wider">
            Rework queue ({rework.length})
          </TabsTrigger>
          <TabsTrigger data-testid="view-scrap" value="scrap" className="rounded-none text-xs uppercase tracking-wider">
            Scrap log ({scrap.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "rework" ? (
        rework.length === 0 ? (
          <EmptyState testid="rework-empty" title="No rework jobs" description="Log a part that failed and needs redoing." />
        ) : (
          <>
            <div className="border border-border bg-white md:hidden" data-testid="rework-cards">
              {rework.map((r) => (
                <MobileRow
                  key={r.id}
                  testid={`rework-card-${r.id}`}
                  title={r.part_name}
                  badges={
                    <>
                      <StatusBadge testid={`rework-status-${r.id}`} value={r.status} />
                      <StatusBadge value={r.reason} />
                    </>
                  }
                  fields={[
                    { label: "Quantity", value: `${r.quantity} ${r.unit}` },
                    { label: "Area", value: r.area || "—" },
                    { label: "Labour", value: `${r.labour_minutes} min` },
                    { label: "Labour cost", value: `$${r.labour_cost}` },
                    { label: "Logged by", value: r.logger_name },
                  ]}
                  media={
                    <div className="mt-3">
                      <CaptionedImage
                        testid={`rework-image-${r.id}`}
                        imageId={r.image_id}
                        caption={r.image_caption}
                        className="h-40 w-full border border-border object-cover"
                      />
                      <Select value={r.status} onValueChange={(v) => setReworkStatus(r.id, v)}>
                        <SelectTrigger data-testid={`rework-status-select-mobile-${r.id}`} className="mt-3 h-10 w-full rounded-none border-slate-300 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {REWORK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  }
                />
              ))}
            </div>

            <div className="hidden overflow-x-auto border border-border bg-white md:block">
              <table className="w-full min-w-[880px] text-sm" data-testid="rework-table">
                <thead>
                  <tr className="border-b border-border bg-slate-50 text-left">
                    {["Part", "Qty", "Reason", "Area", "Labour", "Cost", "Status", "Logged by", "Action"].map((h) => (
                      <th key={h} className="overline px-3 py-2.5 text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rework.map((r) => (
                    <tr key={r.id} data-testid={`rework-row-${r.id}`} className="border-b border-border transition-colors duration-150 last:border-0 hover:bg-slate-50">
                      <td className="px-3 py-2.5 font-medium text-[#0F172A]">
                        <div className="flex items-center gap-2.5">
                          <CaptionedImage
                            testid={`rework-thumb-${r.id}`}
                            imageId={r.image_id}
                            caption={null}
                            className="h-9 w-9 shrink-0 border border-border object-cover"
                          />
                          <span>
                            {r.part_name}
                            {r.image_caption && (
                              <span className="block text-xs font-normal italic text-slate-500">
                                {r.image_caption}
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs">{r.quantity} {r.unit}</td>
                      <td className="px-3 py-2.5"><StatusBadge value={r.reason} /></td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{r.area || "—"}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{r.labour_minutes} min</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-500">${r.labour_cost}</td>
                      <td className="px-3 py-2.5"><StatusBadge testid={`rework-status-${r.id}`} value={r.status} /></td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{r.logger_name}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <Select value={r.status} onValueChange={(v) => setReworkStatus(r.id, v)}>
                            <SelectTrigger data-testid={`rework-status-select-${r.id}`} className="h-8 w-32 rounded-none border-slate-300 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {REWORK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {user?.role === "admin" && (
                            <button
                              data-testid={`delete-rework-${r.id}`}
                              onClick={() => removeRework(r.id)}
                              className="border border-border p-1.5 text-[#FF3B30] transition-colors duration-150 hover:bg-[#FFECEB]"
                            >
                              <Trash size={12} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : scrap.length === 0 ? (
        <EmptyState testid="scrap-empty" title="No scrap logged" description="Record scrapped material to see the cost it takes out of stock." />
      ) : (
        <>
          <div className="border border-border bg-white md:hidden" data-testid="scrap-cards">
            {scrap.map((s) => (
              <MobileRow
                key={s.id}
                testid={`scrap-card-${s.id}`}
                title={s.item_name}
                badges={
                  <>
                    <StatusBadge value={s.reason} />
                    {s.stock_deducted && (
                      <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-[#FF3B30]">
                        Stock deducted
                      </span>
                    )}
                  </>
                }
                fields={[
                  { label: "Quantity", value: `${s.quantity} ${s.unit}` },
                  { label: "Value lost", value: `$${s.scrap_value}`, tone: "danger" },
                  { label: "Area", value: s.area || "—" },
                  { label: "Logged by", value: s.logger_name },
                ]}
                media={
                  <CaptionedImage
                    testid={`scrap-image-${s.id}`}
                    imageId={s.image_id}
                    caption={s.image_caption}
                    className="h-40 w-full border border-border object-cover"
                  />
                }
              />
            ))}
          </div>

          <div className="hidden overflow-x-auto border border-border bg-white md:block">
            <table className="w-full min-w-[860px] text-sm" data-testid="scrap-table">
              <thead>
                <tr className="border-b border-border bg-slate-50 text-left">
                  {["Item", "Qty", "Reason", "Unit cost", "Value lost", "Stock", "Area", "Logged by", ""].map((h) => (
                    <th key={h} className="overline px-3 py-2.5 text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scrap.map((s) => (
                  <tr key={s.id} data-testid={`scrap-row-${s.id}`} className="border-b border-border transition-colors duration-150 last:border-0 hover:bg-slate-50">
                    <td className="px-3 py-2.5 font-medium text-[#0F172A]">
                      <div className="flex items-center gap-2.5">
                        <CaptionedImage
                          testid={`scrap-thumb-${s.id}`}
                          imageId={s.image_id}
                          caption={null}
                          className="h-9 w-9 shrink-0 border border-border object-cover"
                        />
                        <span>
                          {s.item_name}
                          {s.image_caption && (
                            <span className="block text-xs font-normal italic text-slate-500">
                              {s.image_caption}
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs">{s.quantity} {s.unit}</td>
                    <td className="px-3 py-2.5"><StatusBadge value={s.reason} /></td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500">${s.unit_cost}</td>
                    <td data-testid={`scrap-value-${s.id}`} className="px-3 py-2.5 font-mono text-xs font-bold text-[#FF3B30]">
                      ${s.scrap_value}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">
                      {s.stock_deducted ? "Deducted" : "Not deducted"}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{s.area || "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{s.logger_name}</td>
                    <td className="px-3 py-2.5">
                      {user?.role === "admin" && (
                        <button
                          data-testid={`delete-scrap-${s.id}`}
                          onClick={() => removeScrap(s.id)}
                          className="border border-border p-1.5 text-[#FF3B30] transition-colors duration-150 hover:bg-[#FFECEB]"
                        >
                          <Trash size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Log rework */}
      <Dialog open={reworkOpen} onOpenChange={setReworkOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Log rework job</DialogTitle>
            <DialogDescription>Record a part that failed and needs redoing.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRework} className="space-y-4" data-testid="add-rework-form">
            <div>
              <Label className="overline text-slate-500">Part / assembly</Label>
              <Input data-testid="rework-part-input" value={rForm.part_name} onChange={(e) => setRForm({ ...rForm, part_name: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="overline text-slate-500">Linked inventory item (optional)</Label>
              <Select value={rForm.item_id} onValueChange={(v) => setRForm({ ...rForm, item_id: v })}>
                <SelectTrigger data-testid="rework-item-select" className="mt-1.5 rounded-none border-slate-300">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Quantity</Label>
                <Input data-testid="rework-quantity-input" type="number" min="0" step="any" value={rForm.quantity} onChange={(e) => setRForm({ ...rForm, quantity: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Unit</Label>
                <Input data-testid="rework-unit-input" value={rForm.unit} onChange={(e) => setRForm({ ...rForm, unit: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Reason</Label>
                <Select value={rForm.reason} onValueChange={(v) => setRForm({ ...rForm, reason: v })}>
                  <SelectTrigger data-testid="rework-reason-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REWORK_REASONS.map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">Area</Label>
                <Input data-testid="rework-area-input" value={rForm.area} onChange={(e) => setRForm({ ...rForm, area: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Labour (minutes)</Label>
                <Input data-testid="rework-minutes-input" type="number" min="0" value={rForm.labour_minutes} onChange={(e) => setRForm({ ...rForm, labour_minutes: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Labour rate ($/hr)</Label>
                <Input data-testid="rework-rate-input" type="number" min="0" step="0.01" value={rForm.labour_rate} onChange={(e) => setRForm({ ...rForm, labour_rate: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
            </div>
            <div>
              <Label className="overline text-slate-500">Notes</Label>
              <Textarea data-testid="rework-notes-input" value={rForm.notes} onChange={(e) => setRForm({ ...rForm, notes: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <ImageCaptionField
              testid="rework-image"
              value={rForm.image_id}
              caption={rForm.image_caption}
              onChange={({ image_id, image_caption }) => setRForm((f) => ({ ...f, image_id, image_caption }))}
            />
            <DialogFooter>
              <Button data-testid="save-rework-button" type="submit" className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto">
                Log rework
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Log scrap */}
      <Dialog open={scrapOpen} onOpenChange={setScrapOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Log scrapped material</DialogTitle>
            <DialogDescription>Value lost is calculated from the item's unit cost.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitScrap} className="space-y-4" data-testid="add-scrap-form">
            <div>
              <Label className="overline text-slate-500">Inventory item</Label>
              <Select value={sForm.item_id} onValueChange={pickItem}>
                <SelectTrigger data-testid="scrap-item-select" className="mt-1.5 rounded-none border-slate-300">
                  <SelectValue placeholder="Pick an item" />
                </SelectTrigger>
                <SelectContent>
                  {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline text-slate-500">Or type a material name</Label>
              <Input data-testid="scrap-item-input" value={sForm.item_name} onChange={(e) => setSForm({ ...sForm, item_name: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Quantity</Label>
                <Input data-testid="scrap-quantity-input" type="number" min="0" step="any" value={sForm.quantity} onChange={(e) => setSForm({ ...sForm, quantity: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Unit</Label>
                <Input data-testid="scrap-unit-input" value={sForm.unit} onChange={(e) => setSForm({ ...sForm, unit: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Reason</Label>
                <Select value={sForm.reason} onValueChange={(v) => setSForm({ ...sForm, reason: v })}>
                  <SelectTrigger data-testid="scrap-reason-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SCRAP_REASONS.map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">Area</Label>
                <Input data-testid="scrap-area-input" value={sForm.area} onChange={(e) => setSForm({ ...sForm, area: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
            </div>
            <label className="flex items-center gap-2.5 border border-border p-3">
              <Checkbox
                data-testid="scrap-deduct-checkbox"
                checked={sForm.deduct_stock}
                onCheckedChange={(v) => setSForm({ ...sForm, deduct_stock: !!v })}
              />
              <span className="text-sm text-[#0F172A]">Deduct this quantity from inventory</span>
            </label>
            <div>
              <Label className="overline text-slate-500">Notes</Label>
              <Textarea data-testid="scrap-notes-input" value={sForm.notes} onChange={(e) => setSForm({ ...sForm, notes: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <ImageCaptionField
              testid="scrap-image"
              value={sForm.image_id}
              caption={sForm.image_caption}
              onChange={({ image_id, image_caption }) => setSForm((f) => ({ ...f, image_id, image_caption }))}
            />
            <DialogFooter>
              <Button data-testid="save-scrap-button" type="submit" className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto">
                Log scrap
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
